// Custom DS-Table column for Opportunity Stage. Table.TextColumn renders the label and nothing else, so
// it cannot host the EditableCell wrapper the win-probability/stage editor hangs off (spec 008 §2.4).
// This column is otherwise a text column: same accessorKey, same comparator, same clipping behaviour.
// Dist-internal imports are the accepted web-front convention for custom DS columns.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import Styled from 'styled-components';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';

// StyledCell sets `white-space: nowrap` with no containment of its own, so the label clips here rather
// than spilling over the neighbouring columns (the same treatment DateOrAddColumn applies).
const StageText = Styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

export interface StageColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
	// px: the default width (and what a grip double-click resets to) and the drag lower bound.
	size?: number;
	minSize?: number;
	// Wraps the rendered label — the call site supplies the EditableCell (or the label unchanged).
	cellContent: (content: React.ReactNode, row: OpportunityTableRow) => React.ReactNode;
}

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StageColumn = (_: StageColumnType): null => null;

StageColumn.toColumnDef = (child: React.ReactElement<StageColumnType>): ColumnDef<OpportunityTableRow> => {
	const {align, cellContent} = child.props;
	return {
		...defaultColumnDef(child.props),
		accessorKey: child.props.accessorKey,
		sortingFn: child.props.sortingFn,
		cell: ({row}: {row: {original: OpportunityTableRow}}) => (
			<StyledCell align={align} data-cy="opportunity-stage-cell">
				{cellContent(<StageText>{row.original.stageLabel}</StageText>, row.original)}
			</StyledCell>
		),
	};
};

export default StageColumn;
